import { useState } from 'react';
import { Stack, Typography, Divider, Box, Container, Grid, Card } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import axiosInstance from '../utils/axiosInstance';
import EncodeSettings from '../components/encode-settings/encodeSettings';
import ImageSettings from '../components/image-settings/imageSettings';
import SingleSelector from '../components/single-selector/singleSelector';
import VideoPlayer from '../components/video-player/videoPlayer';

export default function CameraConfiguration() {
  const { enqueueSnackbar } = useSnackbar();
  const [camera, setCamera] = useState(null);
  const [cameraSettings, setCameraSettings] = useState(null);
  const [fetchStatus, setFetchStatus] = useState('uninitialized');
  const cameraList = useSelector((state) => state.app.cameraList);

  const fetchCameraSettings = (camera) => {
    if (!camera) {
      return;
    }
    setFetchStatus('pending');
    axiosInstance
      .get(`${window.location.protocol}//${camera.host}/api/v1/sensor/${camera.sensorId}/settings`)
      .then(async (response) => {
        setFetchStatus('success');
        console.debug('fetchSettings response: ', response.data);
        if (Object.prototype.hasOwnProperty.call(response.data, camera.sensorId)) {
          setCameraSettings(response.data[camera.sensorId]);
        } else {
          setCameraSettings(null);
        }
      })
      .catch((error) => {
        setFetchStatus('error');
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
  };

  const handleEncodeSettingModified = (property, value) => {
    console.debug(property, value);
    const newDeviceSettings = JSON.parse(JSON.stringify(cameraSettings));
    newDeviceSettings.Encode[property].Value = property !== 'Resolution' ? value.toString() : value;
    console.debug(newDeviceSettings);
    setCameraSettings(newDeviceSettings);
  };

  const handleImageSettingModified = (property, value) => {
    console.debug(property, value);
    const newDeviceSettings = JSON.parse(JSON.stringify(cameraSettings));
    newDeviceSettings.Image[property].Value = value.toString();
    console.debug(newDeviceSettings);
    setCameraSettings(newDeviceSettings);
  };

  const handleSubmitSettings = () => {
    const updatedSettings = JSON.parse(JSON.stringify(cameraSettings));
    Object.keys(updatedSettings).forEach((group) => {
      const settingGroup = updatedSettings[group];
      Object.keys(settingGroup).forEach((name) => {
        settingGroup[name] = settingGroup[name].Value;
      });
    });
    console.debug('Update settings payload', updatedSettings);
    enqueueSnackbar(`Settings update pending: ${camera.name}`, {
      variant: 'info',
    });
    axiosInstance
      .post(`${window.location.protocol}//${camera.host}/api/v1/sensor/${camera.sensorId}/settings`, updatedSettings)
      .then((response) => {
        const { data } = response;
        enqueueSnackbar(`Settings updated: ${camera.name}`, {
          variant: 'success',
        });
        console.debug('postSettings response: ', data);
      })
      .catch((error) => {
        enqueueSnackbar(`Settings update failed: ${camera.name}`, {
          variant: 'error',
        });
        console.error(error);
      });
  };

  const handleCameraSelect = (cam) => {
    setCamera(cam);
    fetchCameraSettings(cam);
    if (!cam) {
      setCameraSettings(null);
    }
  };

  // Function to filter authorized cameras
  const filterAuthorized = (cameraList) => cameraList
    .filter((camera) => camera.isAuthorized === true);

  const encodeSettingsPresent =
    cameraSettings != null && Object.prototype.hasOwnProperty.call(cameraSettings, 'Encode');
  const imageSettingsPresent = cameraSettings != null && Object.prototype.hasOwnProperty.call(cameraSettings, 'Image');

  return (
    <>
      <Helmet>
        <title> Camera configuration </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Camera configuration
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ mb: 2 }}>
                  <SingleSelector
                    id="camera-configuration"
                    placeholder="Type to search"
                    label="Select camera"
                    value={camera}
                    onChange={handleCameraSelect}
                    getOptionLabel={(option) => {
                      if (option.remoteDeviceName && option.remoteDeviceLocation) {
                        return `${option.name} at ${option.remoteDeviceName} (${option.remoteDeviceLocation})`;
                      } if (option.remoteDeviceName) {
                        return `${option.name} at ${option.remoteDeviceName}`;
                      }
                      return option.name;

                    }}
                    options={filterAuthorized(cameraList)}
                  />
                  {camera != null && encodeSettingsPresent && fetchStatus !== 'pending' && (
                    <Alert severity="success">
                      {Object.keys(cameraSettings.Encode).length} encode settings found for primary stream {camera.name}
                    </Alert>
                  )}
                  {camera != null && imageSettingsPresent && fetchStatus !== 'pending' && (
                    <Alert severity="success">
                      {Object.keys(cameraSettings.Image).length} image settings found for primary stream {camera.name}
                    </Alert>
                  )}
                  {camera != null && imageSettingsPresent == null && fetchStatus !== 'pending' && (
                    <Alert severity="error">Image settings not found for {camera.name}</Alert>
                  )}
                  {camera != null && encodeSettingsPresent == null && fetchStatus !== 'pending' && (
                    <Alert severity="error">Encode settings not found for {camera.name}</Alert>
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                    Live preview
                  </Typography>
                </Divider>
                {camera && (
                  <Box
                    sx={{
                      display: 'block',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      width: { xs: '100%', sm: '100%', md: 600, lg: 700 },
                    }}
                  >
                    <VideoPlayer
                      camera={camera}
                      key={`${camera.sensorId + camera.host + camera.name}_vid_config`}
                      page="configuration"
                      onlyVideo
                    />
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ mb: 2 }}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                      Image settings
                    </Typography>
                  </Divider>
                  {imageSettingsPresent && (
                    <>
                      <ImageSettings
                        imageSettings={cameraSettings.Image}
                        handleSettingModified={handleImageSettingModified}
                      />
                      <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
                        <LoadingButton size="large" variant="contained" onClick={handleSubmitSettings}>
                          Update
                        </LoadingButton>
                      </Box>
                    </>
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ mb: 2 }}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                      Encode settings
                    </Typography>
                  </Divider>
                  {encodeSettingsPresent && (
                    <>
                      <EncodeSettings
                        encodeSettings={cameraSettings.Encode}
                        handleSettingModified={handleEncodeSettingModified}
                      />
                      <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
                        <LoadingButton size="large" variant="contained" onClick={handleSubmitSettings}>
                          Update
                        </LoadingButton>
                      </Box>
                    </>
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
